import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext.js';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';

const CHECK_TEXT = {
    PalcodePlacement: { title: 'PalcodePlacementCheck', empty: 'PalcodePlacementNoErrors' },
    MissingPalCode: { title: 'MissingPalCodeCheck', empty: 'MissingPalCodeNoErrors' },
    WithholdingCardPaymentItem: { title: 'WithholdingCardPaymentItemCheck', empty: 'WithholdingCardPaymentItemNoErrors' },
};

const PalcodePlacementCheckDialog = ({
    open,
    onClose,
    violations = [],
    checkType = 'PalcodePlacement',
    direction = 'rtl',
}) => {
    const { t } = useTranslation();
    const { handleDocumentRowClick } = useHamburgerMenu();

    const isRtl = direction === 'rtl';
    const textAlign = isRtl ? 'right' : 'left';
    const text = CHECK_TEXT[checkType] || CHECK_TEXT.PalcodePlacement;

    const rows = useMemo(
        () => (violations || []).filter((v) => (v.checkType || 'PalcodePlacement') === checkType),
        [violations, checkType]
    );

    // Opens the shared EditModal for the clicked transaction (same mechanism as VatModal /
    // UnprocessedItemsModal). documentType already matches the section keys the edit flow expects
    // ("Incomes" | "Expenses" | "JournalEntries" | "PaymentAcceptances").
    const handleRowClick = (row) => {
        if (!row?.documentId || !row?.documentType) return;
        if (handleDocumentRowClick) {
            handleDocumentRowClick(row.documentId, row.documentType);
        }
    };

    // Highlights the balance-sheet (מאזני / מאזני רכוש קבוע) vs P&L (תוצאתי) palcode type.
    const renderType = (type, missingPalCode, palCode) => {
        if (missingPalCode) {
            return (
                <Chip
                    label={palCode ? `${t('UnknownPalCodeShort')} ${palCode}` : t('NoPalCode')}
                    size="small"
                    sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: '#fff3e0', color: '#e65100' }}
                />
            );
        }
        if (!type) return <span style={{ color: '#999' }}>-</span>;
        const isResult = type === 'תוצאתי';
        return (
            <Chip
                label={type}
                size="small"
                sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    backgroundColor: isResult ? '#ffe0e0' : '#e0f0ff',
                    color: isResult ? '#b71c1c' : '#0d47a1',
                }}
            />
        );
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal} ${modalStyles.modalEdit}`} role="dialog" aria-modal="true">
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t(text.title)}</div>
                                <div />
                            </div>

                            <div className={modalStyles.scroll}>
                                {rows.length === 0 ? (
                                    <div className={modalStyles.emptyState}>
                                        {t(text.empty)}
                                    </div>
                                ) : (
                                    <div className={modalStyles.tableWrap}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small" stickyHeader dir={direction}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align={textAlign}>{t('MonthYear')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Type')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Description')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Reference')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Amount')}</TableCell>
                                                        <TableCell align={textAlign}>{t('SortCode')}</TableCell>
                                                        <TableCell align={textAlign}>{t('DebitAccount')}</TableCell>
                                                        <TableCell align={textAlign}>{t('CreditAccount')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Error')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map((row, index) => (
                                                        <TableRow
                                                            key={`${row.documentId}-${index}`}
                                                            hover
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => handleRowClick(row)}
                                                        >
                                                            <TableCell align={textAlign}>{row.monthYear}</TableCell>
                                                            <TableCell align={textAlign}>{t(row.documentType)}</TableCell>
                                                            <TableCell align={textAlign}>{row.description || '—'}</TableCell>
                                                            <TableCell align={textAlign}>{row.reference || '—'}</TableCell>
                                                            <TableCell align={textAlign}>{beautifyNumber(row.amount)}</TableCell>
                                                            <TableCell align={textAlign}>{row.sortCodeName || '—'}</TableCell>
                                                            <TableCell align={textAlign}>
                                                                <div>{row.debitAccountName || row.debitAccount || '—'}</div>
                                                                {renderType(row.debitPalCodeType, row.debitMissingPalCode, row.debitPalCode)}
                                                            </TableCell>
                                                            <TableCell align={textAlign}>
                                                                <div>{row.creditAccountName || row.creditAccount || '—'}</div>
                                                                {renderType(row.creditPalCodeType, row.creditMissingPalCode, row.creditPalCode)}
                                                            </TableCell>
                                                            <TableCell align={textAlign} sx={{ color: '#b71c1c' }}>
                                                                {t(row.violationReason)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                )}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose}>
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default PalcodePlacementCheckDialog;
